/**
 * MobilePageHeader — `lg:hidden` sticky top header for phones/tablets.
 *
 * Renders the app logo + page title on the left and an optional `right` slot
 * (e.g. RestaurantSelector + notification/filter buttons) on the right, with an
 * optional prev/label/next date-navigation row and a `children` slot for filter
 * chips. Renders nothing at ≥lg — desktop keeps the NavBar + SideBarMenu chrome.
 * Adopted per-page by later mobile tasks; foundation wires it into one reference page.
 *
 * Usage:
 *   <MobilePageHeader title={t('nav.home')} right={<RestaurantSelector />} />
 */
import React from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useAppSettingsStore } from '../../../stores/appSettingsStore'
import { cardBg, borderGrey, primaryText, secondaryText, roundedLG, boldText } from '../../../styling/stylingHelper'
import { cn } from '../../../lib/utils'

interface MobilePageHeaderProps {
  title: string
  /** Optional element rendered between the app mark and the title (e.g. a restaurant logo). */
  titleLeading?: React.ReactNode
  right?: React.ReactNode
  showDateNavigation?: boolean
  dateLabel?: string
  onPrevious?: () => void
  onNext?: () => void
  children?: React.ReactNode
}

const MobilePageHeader: React.FC<MobilePageHeaderProps> = ({
  title,
  titleLeading,
  right,
  showDateNavigation = false,
  dateLabel,
  onPrevious,
  onNext,
  children,
}) => {
  const { t } = useTranslation()
  const darkMode = useAppSettingsStore(s => s.darkMode)

  const navButtonClass = cn('w-8 h-8 flex items-center justify-center border', roundedLG, cardBg, borderGrey, secondaryText)

  return (
    <div className={cn('lg:hidden sticky top-0 z-30 border-b pb-2.5', cardBg, borderGrey)}>
      <div className="flex items-center justify-between gap-3 px-4 pt-2">
        <div className="flex items-center gap-3 min-w-0">
          <img
            src={darkMode ? '/app_logo.png' : '/logo.png'}
            alt={t('nav.title')}
            className={cn('w-10 h-10 flex-shrink-0', darkMode ? 'object-cover rounded-full' : 'object-contain')}
          />
          {titleLeading}
          <h1 className={cn('text-base truncate', boldText, primaryText)}>{title}</h1>
        </div>

        {right && <div className="flex items-center gap-2 flex-shrink-0">{right}</div>}
      </div>

      {showDateNavigation && (
        <div className="flex items-center justify-between gap-2 px-4 pt-2">
          <button
            type="button"
            onClick={onPrevious}
            aria-label={t('mobileHeader.previousPeriod')}
            className={navButtonClass}
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          <span className={cn('text-base font-semibold whitespace-nowrap', secondaryText)}>{dateLabel}</span>

          <button
            type="button"
            onClick={onNext}
            aria-label={t('mobileHeader.nextPeriod')}
            className={navButtonClass}
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      )}

      {children && (
        <div className="mt-2 flex items-center gap-2 px-4 overflow-x-auto">{children}</div>
      )}
    </div>
  )
}

export default MobilePageHeader
